/**
 * Application Configuration - Single source of truth for app settings
 *
 * This file centralizes application-level configuration that was previously
 * scattered across environment variables and hardcoded values.
 *
 * Configuration Sources (in priority order):
 * 1. Environment variables (VITE_*)
 * 2. Default values in this file
 */

export interface AppConfig {
  /** Application metadata */
  app: {
    name: string;
    title: string;
    description: string;
    author: string;
    url: string;
    locale: string;
  };
  /** Data source configuration */
  data: {
    /** Current data fetch mode */
    fetchMode: 'static' | 'api';
    /** API base URL (when fetchMode is 'api') */
    apiBaseUrl: string;
    /** Enable data caching */
    enableCache: boolean;
    /** Cache TTL in milliseconds */
    cacheTtl: number;
  };
  /** Authentication configuration */
  auth: {
    /** Enable PIN gate */
    enabled: boolean;
    /** Secret date for PIN gate (DD-MM-YYYY format) */
    secretDate: string;
    /** Local storage key for auth state */
    storageKey: string;
    /** Auth token value */
    authValue: string;
  };
  /** Map tile configuration */
  map: {
    /** Tile server URL template */
    tileUrl: string;
    /** Tile attribution text */
    attribution: string;
  };
  /** Feature flags for gradual rollout */
  features: {
    /** Enable Time Machine mode */
    timeMachine: boolean;
    /** Enable photo gallery fullscreen */
    photoFullscreen: boolean;
    /** Enable journal view */
    journalView: boolean;
    /** Enable layer filtering */
    layerFiltering: boolean;
    /** Enable marker clustering */
    markerClustering: boolean;
    /** Enable animation replay */
    animationReplay: boolean;
  };
  /** Debug/development settings */
  debug: {
    /** Enable debug logging */
    enabled: boolean;
    /** Show performance metrics */
    showPerformance: boolean;
    /** Skip PIN gate in development */
    bypassAuth: boolean;
  };
}

/**
 * Get environment variable with fallback
 */
function getEnvVar(key: string, fallback: string): string {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return import.meta.env[key] ?? fallback;
  }
  return fallback;
}

/**
 * Get boolean environment variable
 */
function getEnvBool(key: string, fallback: boolean): boolean {
  const value = getEnvVar(key, String(fallback));
  return value === 'true' || value === '1';
}

/**
 * Check if running in personal mode (standalone deployment like olyakolya.love)
 * Personal mode uses static data, SaaS mode uses API
 */
function isPersonalMode(): boolean {
  return getEnvVar('VITE_APP_MODE', 'saas') === 'personal';
}

/**
 * Default application configuration
 */
export const defaultAppConfig: AppConfig = {
  app: {
    name: 'love-map',
    title: 'Our Love Map',
    description: 'Интерактивная карта наших путешествий и воспоминаний',
    author: 'Olya & Kolya',
    url: 'https://olyakolya.love/',
    locale: 'ru',
  },
  data: {
    // Personal mode uses static JSON, SaaS mode uses API
    fetchMode: isPersonalMode() ? 'static' : 'api',
    apiBaseUrl: getEnvVar('VITE_API_BASE_URL', '/api'),
    enableCache: true,
    cacheTtl: 5 * 60 * 1000, // 5 minutes
  },
  auth: {
    enabled: true,
    secretDate: getEnvVar('VITE_SECRET_DATE', '14-01-2025'),
    storageKey: 'love-map-auth',
    authValue: 'unlocked',
  },
  map: {
    tileUrl: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
  },
  features: {
    timeMachine: true,
    photoFullscreen: true,
    journalView: true,
    layerFiltering: true,
    markerClustering: true,
    animationReplay: true,
  },
  debug: {
    enabled: getEnvBool('VITE_DEBUG', false),
    showPerformance: getEnvBool('VITE_SHOW_PERFORMANCE', false),
    bypassAuth: getEnvBool('VITE_BYPASS_AUTH', false),
  },
};

/**
 * Runtime configuration instance
 * Can be modified at runtime for testing or feature flags
 */
let currentConfig: AppConfig = { ...defaultAppConfig };

/**
 * Get the current application configuration
 */
export function getAppConfig(): AppConfig {
  return currentConfig;
}

/**
 * Update configuration at runtime
 * Useful for feature flag updates from server
 */
export function updateAppConfig(updates: Partial<AppConfig>): AppConfig {
  currentConfig = {
    ...currentConfig,
    ...updates,
    app: { ...currentConfig.app, ...updates.app },
    data: { ...currentConfig.data, ...updates.data },
    auth: { ...currentConfig.auth, ...updates.auth },
    map: { ...currentConfig.map, ...updates.map },
    features: { ...currentConfig.features, ...updates.features },
    debug: { ...currentConfig.debug, ...updates.debug },
  };
  return currentConfig;
}

/**
 * Reset configuration to defaults
 */
export function resetAppConfig(): AppConfig {
  currentConfig = { ...defaultAppConfig };
  return currentConfig;
}

/**
 * Check if a feature is enabled
 */
export function isFeatureEnabled(feature: keyof AppConfig['features']): boolean {
  return currentConfig.features[feature];
}

/**
 * Check if debug mode is enabled
 */
export function isDebugMode(): boolean {
  return currentConfig.debug.enabled;
}

export default defaultAppConfig;
